// CB:BEGIN pm-property-detail-page
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';
import SharedHeaderPropertyManager from '../components/SharedHeaderPropertyManager';
import ContentLimiter from '../components/layout/ContentLimiter';
import { Building2, ArrowLeft, ClipboardList } from 'lucide-react';

const BRAND_GREEN = '#123c3b';

interface Property {
  id: string; label?: string; address1: string; address2?: string;
  city: string; state: string; zip: string; propertyType: string; notes?: string;
  _count?: { projects: number };
}

interface Project {
  id: string; address: string; city?: string; state?: string;
  projectType: string; status: string; priority: string;
  requestedResponseBy?: string; createdAt: string; updatedAt?: string;
  bids?: { id: string; amount?: number; isAccepted?: boolean }[];
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  DRAFT: { bg: '#fef3c7', color: '#92400e' }, POSTED: { bg: '#dbeafe', color: '#1d4ed8' },
  BIDS_COLLECTING: { bg: '#fef9c3', color: '#854d0e' }, BIDS_READY: { bg: '#dcfce7', color: '#166534' },
  DECIDING: { bg: '#fef9c3', color: '#854d0e' }, BID_SELECTED: { bg: '#e0f2fe', color: '#0369a1' },
  HOMEOWNER_CONTRACT_SIGNING: { bg: '#f3e8ff', color: '#7e22ce' }, ESCROW_PENDING: { bg: '#fef3c7', color: '#92400e' },
  IN_PROGRESS: { bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P1: { bg: '#e0f2fe', color: '#0369a1' }, ACTIVE_P2: { bg: '#e0f2fe', color: '#0369a1' },
  ACTIVE_P3: { bg: '#e0f2fe', color: '#0369a1' }, COMPLETED: { bg: '#dcfce7', color: '#166534' },
  CANCELLED: { bg: '#fee2e2', color: '#dc2626' },
};

const PRIORITY_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  EMERGENCY: { bg: '#fee2e2', color: '#dc2626', label: 'Emergency' },
  HIGH: { bg: '#fef3c7', color: '#b45309', label: 'High' },
  NORMAL: { bg: '#f1f5f9', color: '#475569', label: 'Normal' },
};

function fmt(d?: string) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
}

function statusLabel(s: string) {
  const m: Record<string, string> = {
    DRAFT: 'Draft', POSTED: 'Posted', BIDS_COLLECTING: 'Collecting Bids', BIDS_READY: 'Bids Ready',
    DECIDING: 'Deciding', BID_SELECTED: 'Contractor Selected', HOMEOWNER_CONTRACT_SIGNING: 'Docs Signing',
    ESCROW_PENDING: 'Escrow Setup', IN_PROGRESS: 'In Progress', ACTIVE_P1: 'Active (P1)',
    ACTIVE_P2: 'Active (P2)', ACTIVE_P3: 'Active (P3)', COMPLETED: 'Completed', CANCELLED: 'Cancelled',
  };
  return m[s] || s;
}

interface Props { onNavigate: (screen: string) => void; }

export default function PropertyManagerPropertyDetailPage({ onNavigate }: Props) {
  const { getToken } = useAuth();
  const [property, setProperty] = useState<Property | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Extract property ID from URL path: /pm/properties/:id
  const propertyId = (() => {
    try {
      const parts = window.location.pathname.split('/');
      const idx = parts.indexOf('properties');
      return idx >= 0 ? parts[idx + 1] : null;
    } catch { return null; }
  })();

  const fetchData = useCallback(async () => {
    if (!propertyId) { setError('Property not found'); setLoading(false); return; }
    setLoading(true);
    try {
      const freshToken = await getToken().catch(() => null);
      if (!freshToken) { setLoading(false); return; }
      const r = await fetch(getApiUrl(`/api/property-manager/properties/${propertyId}`), {
        headers: { Authorization: `Bearer ${freshToken}` }, credentials: 'include',
      });
      if (!r.ok) { setError(r.status === 404 ? 'Property not found' : 'Failed to load property'); return; }
      const d = await r.json();
      setProperty(d.property);
      setProjects(d.projects || []);
    } catch { setError('Failed to load property'); } finally { setLoading(false); }
  }, [getToken, propertyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const completedProjects = projects.filter(p => p.status === 'COMPLETED');
  const activeProjects = projects.filter(p => ['POSTED', 'BIDS_COLLECTING', 'BIDS_READY', 'DECIDING', 'IN_PROGRESS', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3'].includes(p.status));

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-properties" />
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8', fontSize: 14 }}>Loading property…</div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-properties" />
        <ContentLimiter><div style={{ padding: '60px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#dc2626', marginBottom: 12 }}>{error || 'Property not found'}</div>
          <button onClick={() => onNavigate('pm-properties')} style={{ padding: '9px 18px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Back to Properties</button>
        </div></ContentLimiter>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-properties" />

      <ContentLimiter>
        <div style={{ padding: '28px 0' }}>
          {/* Breadcrumb */}
          <button onClick={() => onNavigate('pm-properties')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', padding: '0 0 20px', fontWeight: 500 }}>
            <ArrowLeft size={14} /> Back to Properties
          </button>

          {/* Property header */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '28px 28px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ width: 52, height: 52, borderRadius: 12, background: property.propertyType === 'COMMERCIAL' ? '#eff6ff' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Building2 size={24} color={property.propertyType === 'COMMERCIAL' ? '#3b82f6' : '#16a34a'} />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>
                  {property.label || property.address1}
                </h1>
                <div style={{ fontSize: 14, color: '#64748b' }}>
                  {property.address1}{property.address2 ? `, ${property.address2}` : ''}, {property.city}, {property.state} {property.zip}
                </div>
              </div>
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
              {[
                { label: 'Type', value: property.propertyType.replace(/_/g, ' ') },
                { label: 'Total Projects', value: String(projects.length) },
                { label: 'Active', value: String(activeProjects.length) },
                { label: 'Completed', value: String(completedProjects.length) },
              ].map(s => (
                <div key={s.label} style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 16px', minWidth: 100 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Notes */}
            {property.notes && (
              <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
                <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{property.notes}</div>
              </div>
            )}
          </div>

          {/* Projects section */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f1f5f9' }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Project History ({projects.length})</h2>
              <button onClick={() => onNavigate('pm-dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <ClipboardList size={13} /> New Project
              </button>
            </div>

            {projects.length === 0 ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <ClipboardList size={36} style={{ color: '#cbd5e1', marginBottom: 12 }} />
                <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>No projects yet</div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Create a project for this property to start collecting contractor bids.</div>
                <button onClick={() => onNavigate('pm-dashboard')} style={{ padding: '10px 24px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Create Project</button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {['Work Type', 'Status', 'Priority', 'Bids', 'Response By', 'Created', 'Last Updated'].map(col => (
                        <th key={col} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map(p => {
                      const sc = STATUS_COLORS[p.status] || { bg: '#f1f5f9', color: '#475569' };
                      const pc = PRIORITY_COLORS[p.priority] || PRIORITY_COLORS.NORMAL;
                      const acceptedBid = p.bids?.find(b => b.isAccepted);
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                          <td style={{ padding: '14px 16px', fontSize: 13, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{p.projectType.charAt(0) + p.projectType.slice(1).toLowerCase()}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ display: 'inline-block', background: sc.bg, color: sc.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{statusLabel(p.status)}</span>
                          </td>
                          <td style={{ padding: '14px 16px' }}>
                            <span style={{ display: 'inline-block', background: pc.bg, color: pc.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{pc.label}</span>
                          </td>
                          <td style={{ padding: '14px 16px', fontSize: 13, color: '#374151', textAlign: 'center' }}>
                            <span style={{ fontWeight: p.bids?.length ? 700 : 400 }}>{p.bids?.length ?? 0}</span>
                            {acceptedBid && <div style={{ fontSize: 10, color: '#16a34a', fontWeight: 600, marginTop: 2 }}>✓ Selected</div>}
                          </td>
                          <td style={{ padding: '14px 16px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>{fmt(p.requestedResponseBy)}</td>
                          <td style={{ padding: '14px 16px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmt(p.createdAt)}</td>
                          <td style={{ padding: '14px 16px', fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmt(p.updatedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Activity log placeholder */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '24px', marginTop: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' }}>Activity Log</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {projects.length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>No activity yet for this property.</div>
              ) : (
                [...projects]
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((p, i) => (
                    <div key={p.id} style={{ display: 'flex', gap: 12, paddingBottom: i < projects.length - 1 ? 16 : 0, marginBottom: i < projects.length - 1 ? 16 : 0, borderBottom: i < projects.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[p.status]?.color || '#94a3b8', marginTop: 5, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{p.projectType.charAt(0) + p.projectType.slice(1).toLowerCase()} project created</div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Status: {statusLabel(p.status)} · {p.bids?.length ?? 0} bid{(p.bids?.length ?? 0) !== 1 ? 's' : ''}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmt(p.createdAt)}</div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

        </div>
      </ContentLimiter>
    </div>
  );
}
// CB:END pm-property-detail-page
